import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IStore } from "@/redux/defaultStore";
import {
    hideGameDetectedNotification,
    hideGameExitNotification,
} from "@/redux/meta/metaActions";
import { sendMessageToOW } from "@/hooks/useOverwolfInterop";
import { useGameNotificationPreferences } from "@/hooks/useGameNotificationPreferences";
import { useGameNotificationManager } from "@/hooks/useGameNotificationManager";
import { useGetAllGames } from "@/hooks/overwolf/useGetAllGames";
import GameDetectedNotification from "./GameDetectedNotification";
import GameExitNotification from "./GameExitNotification";

/**
 * Container component that manages all game notifications
 * Handles notification display logic and user interactions
 */
const GameNotificationContainer: React.FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { gameNotifications } = useSelector((store: IStore) => store.metaStore);
    const { updatePreferences } = useGameNotificationPreferences();
    const { games: allGames } = useGetAllGames();

    // Initialize the notification manager to start monitoring game state
    const { markNotificationDismissed } = useGameNotificationManager();

    const handleEnterGameMode = () => {
        if (gameNotifications.currentGameInfo && allGames) {

            // Find the matching game in our games list
            const detectedGameId = gameNotifications.currentGameInfo.gameId;
            const matchingGame = allGames.find(game =>
                game.gameId === detectedGameId ||
                game.classId?.toString() === detectedGameId);

            if (matchingGame) {
                // Navigate to the game-specific view in CockpitV3
                const gameSlug = matchingGame.slug || matchingGame.name.toLowerCase().replace(/\s+/g, "-");
                navigate(`/play?view=games&game=${gameSlug}`, { replace: false });
            } else {
                // Fall back to general games view
                navigate("/play?view=games", { replace: false });
            }

            // Request Overwolf to resize to compact mode for game view
            sendMessageToOW({
                type: "de:request-window-resize",
                payload: { mode: "collapsed" },
            });

            // Hide the notification
            dispatch(hideGameDetectedNotification());
        }
    };

    const handleDismissGameDetected = () => {
        markNotificationDismissed(); // Don't show any more notifications this session
        dispatch(hideGameDetectedNotification());
    };

    const handleDontShowAgainGameDetected = () => {
        // Mark dismissed for this session
        markNotificationDismissed();

        /*
         * Update preferences to disable all game detected notifications
         * This will persist to localStorage and disable notifications permanently
         */
        updatePreferences({ showGameDetectedNotifications: false });

        dispatch(hideGameDetectedNotification());
    };

    const handleExitGameMode = () => {
        // Navigate back to main dashboard (removes game view)
        navigate("/play", { replace: false });

        // Hide the notification
        dispatch(hideGameExitNotification());
    };

    const handleStayInGameMode = () => {
        dispatch(hideGameExitNotification());
    };

    const handleDismissGameExit = () => {
        dispatch(hideGameExitNotification());
    };

    return (
        <div className="game-notification-container">
            {/* Game Detected Notification */}
            {gameNotifications.gameDetectedVisible && gameNotifications.currentGameInfo &&
                <GameDetectedNotification
                    gameInfo={gameNotifications.currentGameInfo}
                    onEnterGameMode={handleEnterGameMode}
                    onDismiss={handleDismissGameDetected}
                    onDontShowAgain={handleDontShowAgainGameDetected}
                />
            }

            {/* Game Exit Notification */}
            {gameNotifications.gameExitVisible && gameNotifications.lastGameExitInfo &&
                <GameExitNotification
                    gameInfo={gameNotifications.lastGameExitInfo.gameInfo}
                    sessionDuration={gameNotifications.lastGameExitInfo.sessionDuration}
                    xpEarned={gameNotifications.lastGameExitInfo.xpEarned}
                    questsCompleted={gameNotifications.lastGameExitInfo.questsCompleted}
                    onExitGameMode={handleExitGameMode}
                    onStayInGameMode={handleStayInGameMode}
                    onDismiss={handleDismissGameExit}
                />
            }
        </div>
    );
};

export default GameNotificationContainer;